import type { ChartLineOptions } from '~/components/nrjx/chart/line.vue';
import type { TooltipData } from '~/components/nrjx/chart/tooltip/index.vue';

export const ObjectiveOptionChart = () => {
  // Composables
  const dayjs = useDayjs();
  const { formatNumberToIsoNumber } = useNumbers();
  const { t } = useI18n();
  const { getTailwindColor } = useTailwind();

  // Methods
  const options = (options: {
    milestones: {
      timestamp: Date;
      consumption_target: number;
      cost_target: number;
    }[];
    unit: 'kwh' | 'euro';
    reference_year: number;
    referenceYearConsumption: ApiConsumptionResponse['items'] | null;
    currentYearConsumption: ApiConsumptionResponse['items'] | null;
  }): ChartLineOptions => {
    const labels = options.milestones.map((item) => dayjs(item.timestamp).format('MMMM YYYY'));
    return {
      x: {
        ticks: {
          callback: (value: number) => {
            if (value === 0 || !labels[value] || value >= labels.length - 1) return '';

            return labels[value];
          },
        },
      },
      tooltip: (context) => {
        const rawObjectiveIndex = context[0]?.index;
        if (typeof rawObjectiveIndex !== 'number') return null;
        if (rawObjectiveIndex === 0) return null;
        const objectiveIndex = rawObjectiveIndex - 1;

        const currentYearConsumption = options.currentYearConsumption?.at(objectiveIndex);
        const referenceYearConsumption = options.referenceYearConsumption?.at(objectiveIndex);
        const milestone = options.milestones[objectiveIndex + 1];

        // Calculate percentage for title
        let titlePercentage: number | null = null;

        if (currentYearConsumption && milestone) {
          const currentValue = currentYearConsumption.consumption.quantity;
          const objectiveValue = milestone.consumption_target;
          titlePercentage = currentValue ? (currentValue / objectiveValue - 1) * 100 : null;
        }

        const tooltipData: TooltipData = {
          subtitle: titlePercentage ? `${titlePercentage > 0 ? '+' : ''}${formatNumberToIsoNumber(titlePercentage, 1)}%` : undefined,
          body: [
            {
              color: getTailwindColor('blue-600'),
              title: t('overview.objective'),
              value: (() => {
                switch (options.unit) {
                  case 'kwh':
                    return `${formatNumberToIsoNumber(Number(milestone?.consumption_target)) ?? 0} kWh`;
                  case 'euro':
                    return `${formatNumberToIsoNumber(Number(milestone?.cost_target)) ?? 0} €`;
                }
              })(),
            },
            {
              color: getTailwindColor('emerald-500'),
              title: dayjs(options.currentYearConsumption?.[0]?.start).format('YYYY'),
              value: (() => {
                if (!currentYearConsumption) return '';
                switch (options.unit) {
                  case 'kwh':
                    return `${formatNumberToIsoNumber(currentYearConsumption.consumption.quantity ?? 0)} kWh`;
                  case 'euro':
                    return `${formatNumberToIsoNumber(Number(currentYearConsumption.consumption.cost ?? 0))} €`;
                }
              })(),
            },
          ],
          footer: labels[objectiveIndex],
        };

        if (options.referenceYearConsumption && tooltipData.body) {
          tooltipData.body.push({
            color: getTailwindColor('orange-400'),
            title: options.reference_year?.toString() ?? '',
            value: (() => {
              switch (options.unit) {
                case 'kwh':
                  return `${formatNumberToIsoNumber(Number(referenceYearConsumption?.consumption.quantity ?? 0))} kWh`;
                case 'euro':
                  return `${formatNumberToIsoNumber(Number(referenceYearConsumption?.consumption.cost ?? 0))} €`;
              }
            })(),
          });
        }

        return tooltipData;
      },
    };
  };

  return {
    options,
  };
};
